/**
 * Tells the app when it has gone behind and when it has come back.
 *
 * Two sources, because neither is enough on its own. Capacitor's appStateChange
 * is the honest native answer on a phone and fires when the activity is paused
 * or resumed. The page's own visibilitychange is what we get in a browser, and
 * it also covers the phone case where the screen locks without the activity
 * being paused. They agree far more often than not, and the store ignores a
 * repeat of the state it is already in, so a doubled event costs nothing.
 *
 * @capacitor/app is imported lazily and only on a device, so the web build does
 * not carry a native plugin it can never call.
 */
import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { useAppActiveStore } from '@/store/appActive';

export function AppStateRunner() {
  useEffect(() => {
    const setActive = (v: boolean) => useAppActiveStore.getState().setActive(v);
    const onVisibility = () => setActive(!document.hidden);
    document.addEventListener('visibilitychange', onVisibility);

    let cancelled = false;
    let removeNative: (() => void) | null = null;
    if (Capacitor.isNativePlatform()) {
      void (async () => {
        try {
          const { App } = await import('@capacitor/app');
          const handle = await App.addListener('appStateChange', ({ isActive }) => {
            setActive(isActive);
          });
          // Unmounted while we were awaiting: take the listener straight back off
          // rather than leaving one bound to a component that has gone.
          if (cancelled) void handle.remove();
          else removeNative = () => void handle.remove();
        } catch {
          // No plugin, or it refused to register. visibilitychange still covers us.
        }
      })();
    }

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      removeNative?.();
    };
  }, []);

  return null;
}
